// Estado del centro de notificaciones — ADR-2026-08-31-centro-de-notificaciones.
//
// **Por qué esto es un store de módulo y no un `useState` adentro del hook.**
// La campana se monta en TRES lugares a la vez (la fila de cuenta de la home, la
// sidebar del shell y el header flotante de mobile) y la pantalla `/notificaciones`
// suma un cuarto consumidor. Con el estado adentro del hook, cada montaje abriría su
// propio canal de realtime y su propio intervalo, y —peor— al marcar todas como
// leídas desde el popover, la campana de la sidebar seguiría mostrando el badge
// hasta el siguiente refresco. Con un store compartido hay UNA suscripción y un solo
// número, y todos los que lo dibujan cambian juntos.
//
// No se usó un Context porque el Provider tendría que envolver el árbol autenticado
// entero en `App.tsx` (que ya tiene bastante) sin ganar nada: no hay dos árboles con
// notificaciones distintas.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  escucharTapDelPush,
  marcarPendientesDelPush,
  cargarNotificaciones,
  contarSinLeer,
  marcarLeidas,
  silenciarLoVistoEnPantalla,
  estuvoEnAtlasTodoElRefresco,
  TOPE_PANEL,
  type Notificacion,
} from '../lib/notificaciones';
import { escucharMirando, estaMirandoAhora } from '../lib/mirando';

interface EstadoNotificaciones {
  /** Las últimas `TOPE_PANEL`, que es lo que dibuja el popover. */
  items: Notificacion[];
  sinLeer: number;
  /** `false` hasta la primera respuesta. El badge no se dibuja mientras tanto. */
  cargado: boolean;
  cargando: boolean;
}

const ESTADO_INICIAL: EstadoNotificaciones = {
  items: [],
  sinLeer: 0,
  cargado: false,
  cargando: false,
};

let estado: EstadoNotificaciones = ESTADO_INICIAL;
const oyentes = new Set<(e: EstadoNotificaciones) => void>();

function publicar(parcial: Partial<EstadoNotificaciones>) {
  estado = { ...estado, ...parcial };
  oyentes.forEach((f) => f(estado));
}

// ── Ciclo de vida de la conexión ────────────────────────────────────────────
// El primer consumidor la levanta; el último la baja. Un contador y no un boolean:
// con tres campanas montadas, la primera en desmontarse dejaría a las otras dos sin
// realtime.
let consumidores = 0;
let canal: ReturnType<typeof supabase.channel> | null = null;
let intervalo: ReturnType<typeof setInterval> | null = null;
let quitarFoco: (() => void) | null = null;
let quitarTapDelPush: (() => void) | null = null;
// Dueño del store y "generación" de la conexión. El store es de módulo, así que
// sobrevive a un cambio de cuenta sin recarga: el logout de la app recarga la página
// (`lib/cerrarSesion`), pero OTRA pestaña puede cerrar sesión o entrar con otro
// usuario, y Supabase se lo cuenta a ésta por `onAuthStateChange` sin recargar nada.
// Cada `await` de `conectar`/`refrescar` compara su generación con la actual y, si la
// conexión se bajó o se rehizo mientras tanto, tira lo que traía en vez de publicarlo
// como si fuera de la cuenta nueva.
let generacion = 0;
let uidConectado: string | null = null;
let quitarAuth: (() => void) | null = null;
let quitarMirando: (() => void) | null = null;
/**
 * Los ids que había en la campana cuando la persona llegó a Atlas (ver
 * `silenciarLoVistoEnPantalla`). `null` = no está en Atlas, o todavía no se armó: la próxima
 * vuelta lo arma sin marcar nada.
 */
let vistasAlLlegar: Set<string> | null = null;
/** Cuántas veces la persona entró o salió de Atlas (ver `estuvoEnAtlasTodoElRefresco`). */
let vueltaEnAtlas = 0;
/**
 * Un refresco pedido mientras otro estaba en curso. Antes se descartaba; ahora deja pedida UNA
 * vuelta más. Importa al volver a Atlas: si el refresco que arma `vistasAlLlegar` se perdía, lo
 * que terminara después mientras la persona mira quedaba con puntito.
 */
let otraVuelta: { lista: boolean } | null = null;

/**
 * El polling existe aunque haya realtime, y no al revés.
 *
 * OJO: en este proyecto el realtime NO es una garantía. La publicación
 * `supabase_realtime` sólo incluye las tablas que se agregaron a mano — de hecho la
 * suscripción a `company_profiles` de `App.tsx` nunca recibió un evento por eso, y
 * funciona gracias a su polling de respaldo. `notificaciones` SÍ está publicada, pero
 * un canal puede caerse, quedar sin reconectar tras un sleep del sistema o no llegar
 * nunca en una red que corta websockets. 60s es la red de seguridad; en el camino
 * feliz el evento llega antes y el intervalo no hace más que confirmar el número.
 */
const MS_POLLING = 60_000;

async function refrescar(incluirLista = true) {
  if (estado.cargando) {
    otraVuelta = { lista: incluirLista || (otraVuelta?.lista ?? false) };
    return;
  }
  const g = generacion;
  const alEmpezar = { enAtlas: estaMirandoAhora(), vuelta: vueltaEnAtlas };
  publicar({ cargando: true });
  try {
    if (incluirLista) {
      const [pagina, sinLeer] = await Promise.all([
        cargarNotificaciones({ limite: TOPE_PANEL }),
        contarSinLeer(),
      ]);
      if (g !== generacion) return; // la conexión cambió de dueño mientras se esperaba
      // Lo de generación que terminó con la persona en Atlas se marca leído ANTES de publicar, así
      // el puntito no llega a dibujarse.
      // Sólo si estuvo en Atlas de punta a punta: una lista traída mientras estaba afuera no sirve
      // para saber qué vio (la descarta y la arma el refresco que quedó pedido al volver).
      const enAtlas = estuvoEnAtlasTodoElRefresco(alEmpezar, {
        enAtlas: estaMirandoAhora(),
        vuelta: vueltaEnAtlas,
      });
      const { silenciar, vistas } = silenciarLoVistoEnPantalla(pagina.items, vistasAlLlegar, enAtlas);
      vistasAlLlegar = vistas;
      if (silenciar.length === 0) {
        publicar({ items: pagina.items, sinLeer, cargado: true, cargando: false });
      } else {
        const ids = new Set(silenciar);
        publicar({
          items: pagina.items.map((n) => (ids.has(n.id) ? { ...n, leida: true } : n)),
          sinLeer: Math.max(0, sinLeer - silenciar.length),
          cargado: true,
          cargando: false,
        });
        // Si no se pudo marcar, el conteo real vuelve a mostrar el puntito: lo peor que pasa es
        // lo de antes, un puntito de más. Nunca se pierde un aviso.
        // Con la lista entera: si sólo se corrigiera el número, las filas quedarían pintadas como
        // leídas hasta el próximo refresco completo (review #157).
        marcarLeidas(silenciar).catch(() => {
          if (g === generacion) void refrescar();
        });
      }
    } else {
      const sinLeer = await contarSinLeer();
      if (g !== generacion) return;
      publicar({ sinLeer, cargado: true, cargando: false });
    }
  } catch {
    // Sin ruido: un error acá deja la campana como estaba. Es información
    // accesoria — romper la pantalla porque no se pudo contar avisos sería peor
    // que no mostrar el badge.
    if (g !== generacion) return;
    publicar({ cargando: false });
  } finally {
    // La vuelta que se pidió mientras ésta corría. Fuera del `if` de generación: si la conexión
    // cambió de dueño, `reconectar` ya limpió `otraVuelta` y esto no hace nada.
    if (otraVuelta && !estado.cargando) {
      const { lista } = otraVuelta;
      otraVuelta = null;
      void refrescar(lista);
    }
  }
}

/**
 * Marca las notificaciones que se tocaron desde el celular y, si marcó alguna, refresca.
 *
 * Va acá y no en el arranque de la app porque necesita sesión: cuando el push abre Atlas cerrada,
 * la sesión de Supabase todavía no existe. `capturarNotificacionDelPush` (en main.tsx) solo guarda
 * el id; recién en este punto hay con qué marcarlo.
 */
async function marcarLoQueSeTocoDesdeElPush() {
  if (await marcarPendientesDelPush()) await refrescar();
}

async function conectar() {
  const g = generacion;
  await refrescar();
  if (g !== generacion) return;

  const { data: sesion } = await supabase.auth.getUser();
  if (g !== generacion) return;
  const uid = sesion?.user?.id ?? null;
  uidConectado = uid;
  if (!uid) return;

  // Con sesión ya hay con qué marcar. Antes de suscribirse, para que el badge no muestre por un
  // instante un aviso que la persona ya vio en el celular.
  await marcarLoQueSeTocoDesdeElPush();
  if (g !== generacion) return;

  const { data: perfil } = await supabase
    .from('profiles')
    .select('id_company')
    .eq('id', uid)
    .maybeSingle();
  if (g !== generacion) return;
  const idCompany = perfil?.id_company as string | undefined;
  // Sin empresa no hay a qué suscribirse. El polling ya quedó armado igual, así que
  // si la fila aparece más tarde (alta recién hecha) el badge se entera en el
  // siguiente ciclo.
  if (!idCompany || canal) return;

  // Con filtro por empresa y no a secas: sin él, cada INSERT de cualquier cuenta
  // despertaría a todos los clientes conectados.
  canal = supabase
    .channel(`notificaciones-${idCompany}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notificaciones',
        filter: `id_company=eq.${idCompany}`,
      },
      // El payload se ignora a propósito: la fila que llega no trae el estado de
      // lectura (vive en otra tabla) ni respeta el orden con lo que ya está en
      // memoria. El evento es una SEÑAL de "algo cambió"; la verdad la trae la RPC.
      () => { void refrescar(); },
    )
    .subscribe();
}

function soltarCanal() {
  if (canal) {
    try { void supabase.removeChannel(canal); } catch { /* el canal ya no existe */ }
    canal = null;
  }
}

function desconectar() {
  generacion += 1; // lo que esté en vuelo ya no tiene a quién publicarle
  soltarCanal();
  if (intervalo) { clearInterval(intervalo); intervalo = null; }
  if (quitarFoco) { quitarFoco(); quitarFoco = null; }
  if (quitarTapDelPush) { quitarTapDelPush(); quitarTapDelPush = null; }
  if (quitarAuth) { quitarAuth(); quitarAuth = null; }
  if (quitarMirando) { quitarMirando(); quitarMirando = null; }
  uidConectado = null;
  vistasAlLlegar = null;
  otraVuelta = null;
  estado = ESTADO_INICIAL;
}

/**
 * Cambió el dueño de la sesión sin que la campana se desmonte: se tira lo que había
 * (lista, badge y el canal de la empresa anterior) y se vuelve a conectar como la
 * cuenta nueva. El polling y los listeners de foco y del push quedan: son de la
 * pestaña, no de la cuenta.
 */
function reconectar() {
  if (consumidores === 0) return; // ya se desconectó todo; no hay a quién servirle
  generacion += 1;
  soltarCanal();
  uidConectado = null;
  vistasAlLlegar = null; // lo que "había al llegar" era de la cuenta anterior
  otraVuelta = null;
  publicar(ESTADO_INICIAL);
  void conectar();
}

function arrancar() {
  consumidores += 1;
  if (consumidores > 1) return;

  void conectar();

  // Otra pestaña puede cerrar sesión o entrar con otra cuenta, y Supabase se lo
  // cuenta a ésta por `onAuthStateChange` sin recargarla. Si el usuario ya no es el
  // que conectó, se rehace la conexión. `INITIAL_SESSION` se ignora (es el mismo
  // usuario que `conectar` está resolviendo) y el trabajo se difiere fuera del
  // callback: llamar a la API de auth desde adentro puede trabar supabase-js.
  const { data: auth } = supabase.auth.onAuthStateChange((evento, sesion) => {
    if (evento === 'INITIAL_SESSION') return;
    const uid = sesion?.user?.id ?? null;
    if (uid === uidConectado) return;
    setTimeout(reconectar, 0);
  });
  quitarAuth = () => auth.subscription.unsubscribe();

  // Caso "la app YA estaba abierta": la pestaña no vuelve a arrancar, así que el parámetro de la
  // URL no alcanza y el service worker avisa por mensaje.
  quitarTapDelPush = escucharTapDelPush(() => { void marcarLoQueSeTocoDesdeElPush(); });
  intervalo = setInterval(() => { void refrescar(); }, MS_POLLING);

  // Volver a la pestaña es el momento en que el usuario MIRA la campana, así que es
  // cuando más importa que el número esté al día — y es justo cuando el intervalo
  // pudo haber quedado dormido (los navegadores los estrangulan en background).
  const alVolver = () => { if (document.visibilityState === 'visible') void refrescar(); };
  document.addEventListener('visibilitychange', alVolver);
  quitarFoco = () => document.removeEventListener('visibilitychange', alVolver);

  // Llegar a Atlas (o irse) cambia qué cuenta como "visto en pantalla". Al llegar, un refresco arma
  // lo que había; al irse, se olvida, así lo que termine mientras no está queda con puntito.
  quitarMirando = escucharMirando((enAtlas) => {
    vueltaEnAtlas += 1;
    vistasAlLlegar = null;
    if (enAtlas) void refrescar();
  });
}

function parar() {
  consumidores = Math.max(0, consumidores - 1);
  if (consumidores === 0) desconectar();
}

export interface UseNotificaciones extends EstadoNotificaciones {
  /** Marca todas como leídas. Optimista: el badge se apaga en el acto. */
  marcarTodas: () => Promise<void>;
  /** Marca una sola — lo que hace el tap sobre una fila antes de navegar. */
  marcarUna: (id: string) => Promise<void>;
  refrescar: () => Promise<void>;
}

export function useNotificaciones(): UseNotificaciones {
  const [local, setLocal] = useState(estado);

  useEffect(() => {
    const oyente = (e: EstadoNotificaciones) => setLocal(e);
    oyentes.add(oyente);
    arrancar();
    // El store puede estar poblado por otro consumidor que ya llegó: sin esta
    // sincronización, una campana que se monta después arrancaría en cero hasta el
    // siguiente evento.
    setLocal(estado);
    return () => {
      oyentes.delete(oyente);
      parar();
    };
  }, []);

  const marcarTodas = useCallback(async () => {
    // Optimista: el usuario ya vio la lista, el badge tiene que apagarse en el
    // mismo frame en que toca el botón. Si la RPC falla, el refresco de abajo
    // devuelve el número real.
    publicar({
      sinLeer: 0,
      items: estado.items.map((n) => ({ ...n, leida: true })),
    });
    try {
      await marcarLeidas();
    } finally {
      await refrescar(false);
    }
  }, []);

  const marcarUna = useCallback(async (id: string) => {
    const yaLeida = estado.items.find((n) => n.id === id)?.leida;
    if (yaLeida) return;
    publicar({
      sinLeer: Math.max(0, estado.sinLeer - 1),
      items: estado.items.map((n) => (n.id === id ? { ...n, leida: true } : n)),
    });
    try {
      await marcarLeidas([id]);
    } finally {
      await refrescar(false);
    }
  }, []);

  return {
    ...local,
    marcarTodas,
    marcarUna,
    refrescar: () => refrescar(),
  };
}

export default useNotificaciones;
